import React from 'react';
import { SimulationConfig } from '@/types';
import { Zap, Shield, Cpu, Gauge, Wind, Target, Globe, Lock as LucideLock } from 'lucide-react';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { SectionLabel } from './SectionLabel';
import { motion, AnimatePresence } from 'motion/react';
import { ControlGroup } from './ControlGroup';
import { InfoPopup } from './InfoPopup';
import { CollapsibleSection } from './CollapsibleSection';
import { IntegrationTestPanel } from '@/components/IntegrationTestPanel';

interface OptimizationSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  minPlan?: string;
  onUpgradeClick?: () => void;
  isSettingLocked?: (id: string) => boolean;
  getLockInfo?: (id: string) => any;
  ballPoolRef?: React.MutableRefObject<any>;
  trailCirclePoolRef?: React.MutableRefObject<any>;
  collisionEventPoolRef?: React.MutableRefObject<any>;
  blastEventPoolRef?: React.MutableRefObject<any>;
  workerRef?: React.MutableRefObject<Worker | null>;
}

export const OptimizationSettingsSection: React.FC<OptimizationSettingsSectionProps> = ({ 
    config, setConfig, isLocked, minPlan = "ELITE", onUpgradeClick, isSettingLocked, getLockInfo,
    ballPoolRef, trailCirclePoolRef, collisionEventPoolRef, blastEventPoolRef, workerRef
}) => {
  const isEscape = config.mode === 'ESCAPE' || config.mode === 'RHYTHMIC_ESCAPE';

  return (
    <CollapsibleSection 
        title="Optimization" 
        icon={Cpu} 
        darkMode={config.darkMode} 
        settingKey="optimizationSettings"
        isLocked={isLocked}
        lockRequiredPlan={minPlan as any}
        onUpgradeClick={onUpgradeClick}
    >
        <div className="flex flex-col gap-10 mt-6 text-left">
            {/* General Performance */}
            <div className="space-y-6">
                <div className="flex items-center gap-2 mb-2">
                    <Cpu className="w-3.5 h-3.5 opacity-20" />
                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">General</h4>
                </div>
                <div className="grid grid-cols-1 gap-3">
                    <OptCard 
                        label="Smart Render"
                        settingKey="enableFrustumCulling"
                        active={config.enableFrustumCulling}
                        onToggle={() => setConfig(p => ({...p, enableFrustumCulling: !p.enableFrustumCulling}))}
                        darkMode={config.darkMode}
                        desc="Auto-hide off-screen objects"
                        icon={Cpu}
                    />
                    <OptCard 
                        label="Fast Spatial Hash"
                        settingKey="useNumericGridKeys"
                        active={config.useNumericGridKeys}
                        onToggle={() => setConfig(p => ({...p, useNumericGridKeys: !p.useNumericGridKeys}))}
                        darkMode={config.darkMode}
                        desc="Zero-allocation physics grid"
                        icon={Zap}
                    />
                </div>

                <div className="space-y-4">
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <SectionLabel darkMode={config.darkMode} icon="fa-gauge-high">Simulation Performance Lock</SectionLabel>
                            <InfoPopup settingKey="simulationFPS" darkMode={config.darkMode} />
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                            {[0, 30, 60, 120].map(fps => {
                                const active = (config.simulationFPS || 0) === fps;
                                return (
                                    <button
                                        key={fps}
                                        onClick={() => setConfig({ ...config, simulationFPS: fps as any })}
                                        className={`p-3 rounded-xl border text-[9px] font-black uppercase tracking-widest transition-all ${active ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20 shadow-cyan-500/5' : (config.darkMode ? 'bg-white/5 border-white/5 text-zinc-500 hover:bg-white/10' : 'bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50 shadow-sm')}`}
                                    >
                                        {fps === 0 ? 'Max' : `${fps} FPS`}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <SmartInput label="Safety Buffer" settingKey="frustumCullingMargin" min={0} max={1000} value={config.frustumCullingMargin} onChange={(v: number) => setConfig(p => ({...p, frustumCullingMargin: v}))} darkMode={config.darkMode} disabled={!config.enableFrustumCulling} />
                    <SmartInput label="Physics Quality" settingKey="physicsSubsteps" min={1} max={16} value={config.physicsSubsteps} onChange={(v: number) => setConfig(p => ({...p, physicsSubsteps: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Blast Limit" settingKey="maxActiveBlasts" min={10} max={1000} value={config.maxActiveBlasts} onChange={(v: number) => setConfig(p => ({...p, maxActiveBlasts: v}))} darkMode={config.darkMode} />
                </div>
            </div>

            {/* Turbo Mode (Conditional) */}
            {isEscape && (
                <div className="space-y-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Cpu className="w-3.5 h-3.5 text-amber-500" />
                            <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-40">Turbo Mode</h4>
                            <InfoPopup settingKey="enableHyperSpeed" darkMode={config.darkMode} />
                        </div>
                        <Toggle active={config.enableHyperSpeed} onClick={() => setConfig(p => ({...p, enableHyperSpeed: !p.enableHyperSpeed}))} color="bg-amber-600" />
                    </div>

                    <AnimatePresence>
                        {config.enableHyperSpeed && (
                            <motion.div 
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="space-y-6 overflow-hidden"
                            >
                                <div className="grid grid-cols-2 gap-3">
                                    <OptCard small label="Simplified FX" settingKey="hyperSpeedSimplifiedVisuals" active={config.hyperSpeedSimplifiedVisuals} onToggle={() => setConfig(p => ({...p, hyperSpeedSimplifiedVisuals: !p.hyperSpeedSimplifiedVisuals}))} darkMode={config.darkMode} />
                                    <OptCard small label="Auto-Tune" settingKey="hyperSpeedAutoTune" active={config.hyperSpeedAutoTune} onToggle={() => setConfig(p => ({...p, hyperSpeedAutoTune: !p.hyperSpeedAutoTune}))} darkMode={config.darkMode} />
                                </div>

                                <div className="space-y-4">
                                    <SmartInput label="Batch Density" settingKey="hyperSpeedBatchSize" min={10} max={1000} value={config.hyperSpeedBatchSize} onChange={(v: number) => setConfig(p => ({...p, hyperSpeedBatchSize: v}))} darkMode={config.darkMode} />
                                    <SmartInput label="Draw Cap" settingKey="hyperSpeedRenderLimit" min={10} max={500} value={config.hyperSpeedRenderLimit} onChange={(v: number) => setConfig(p => ({...p, hyperSpeedRenderLimit: v}))} darkMode={config.darkMode} />
                                    <SmartInput label="Step Precision" settingKey="hyperSpeedTargetTime" min={1} max={60} value={config.hyperSpeedTargetTime} onChange={(v: number) => setConfig(p => ({...p, hyperSpeedTargetTime: v}))} darkMode={config.darkMode} />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}

            {/* Render Limits */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                    <Cpu className="w-3.5 h-3.5 opacity-20" />
                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Quality Limits</h4>
                </div>

                <div className="grid grid-cols-1 gap-2">
                    <MiniToggle label="Cull Rings" settingKey="cullRingsOutsideScreen" darkMode={config.darkMode} active={config.cullRingsOutsideScreen} onToggle={() => setConfig(p => ({...p, cullRingsOutsideScreen: !p.cullRingsOutsideScreen}))} />
                    <MiniToggle label="Cull Blasts" settingKey="cullBlastsOutsideScreen" darkMode={config.darkMode} active={config.cullBlastsOutsideScreen} onToggle={() => setConfig(p => ({...p, cullBlastsOutsideScreen: !p.cullBlastsOutsideScreen}))} />
                    <MiniToggle label="Fragment Density" settingKey="partialRingBlastOptimization" darkMode={config.darkMode} active={config.partialRingBlastOptimization} onToggle={() => setConfig(p => ({...p, partialRingBlastOptimization: !p.partialRingBlastOptimization}))} />
                </div>
            </div>

            {/* Expert Mode */}
            <div className="space-y-4 pt-4 border-t border-zinc-500/10">
                <div className="flex items-center gap-2 mb-2">
                    <Cpu className="w-3.5 h-3.5 opacity-20" />
                    <h4 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Expert Mode</h4>
                </div>
                <div className="grid grid-cols-1 gap-2">
                    <div className="flex items-center justify-between p-4 rounded-2xl border border-zinc-500/10 bg-zinc-500/5 opacity-40 grayscale pointer-events-none relative overflow-hidden group">
                        <div className="flex items-center gap-3">
                            <Cpu className="w-4 h-4" />
                            <div className="flex flex-col items-start gap-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-black uppercase tracking-tight leading-none text-zinc-500">Cloud Engine</span>
                                    <span className="text-[7px] font-black tracking-widest bg-zinc-500/10 text-zinc-600 px-2 py-0.5 rounded-full">FUTURE DEVELOPMENT</span>
                                </div>
                                <span className="text-[8px] font-bold opacity-40 uppercase">Remote rendering pipeline (Coming Soon)</span>
                            </div>
                        </div>
                        <Shield className="w-4 h-4 text-zinc-400 opacity-30" />
                    </div>
                </div>
            </div>

            {/* Quality & Integrity Self-Tests Suite */}
            <div className="space-y-4 pt-4 border-t border-zinc-500/10">
                <IntegrationTestPanel 
                    darkMode={config.darkMode}
                    pools={{
                        ballPool: ballPoolRef?.current,
                        trailPool: trailCirclePoolRef?.current,
                        collisionPool: collisionEventPoolRef?.current,
                        blastPool: blastEventPoolRef?.current
                    }}
                    activeWorker={workerRef?.current || null}
                />
            </div>
        </div>
    </CollapsibleSection>
  );
};

const OptCard = ({ label, active, onToggle, darkMode, desc, icon: Icon, small, settingKey, isLocked, minPlan, onUpgradeClick }: any) => (
    <button 
        onClick={isLocked ? onUpgradeClick : onToggle}
        className={`flex items-center justify-between transition-all duration-300 border ${small ? 'p-3 rounded-xl' : 'p-4 rounded-2xl'} ${isLocked ? 'opacity-50 cursor-not-allowed bg-zinc-100/50' : active ? (darkMode ? 'bg-white/10 border-white/10' : 'bg-white border-zinc-950/10 shadow-sm') : 'bg-transparent border-transparent opacity-40 hover:opacity-100 hover:bg-zinc-500/5'}`}
    >
        <div className="flex items-center gap-3">
            {Icon && <Icon className={`w-4 h-4 ${isLocked ? 'text-amber-500' : ''}`} />}
            <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black uppercase tracking-tight leading-none">{label}</span>
                    {isLocked && <Shield className="w-2.5 h-2.5 text-amber-500" />}
                    <InfoPopup settingKey={settingKey} darkMode={darkMode} />
                </div>
                {isLocked ? (
                    <span className="text-[7px] font-black uppercase text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded-full">{minPlan || 'PRO'} REQUIRED</span>
                ) : desc && (
                    <span className="text-[8px] font-bold opacity-40 uppercase">{desc}</span>
                )}
            </div>
        </div>
        {isLocked ? (
            <div className="w-8 h-8 rounded-full bg-zinc-950 flex items-center justify-center scale-75">
                <LucideLock className="w-3 h-3 text-white" />
            </div>
        ) : (
            <Toggle active={active} onClick={(e?: React.MouseEvent) => { e?.stopPropagation(); onToggle(); }} />
        )}
    </button>
);

const MiniToggle = ({ label, active, onToggle, settingKey, darkMode }: any) => (
    <div className="flex items-center justify-between p-3 rounded-xl bg-transparent hover:bg-black/5 transition-all">
        <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold opacity-60 uppercase">{label}</span>
            <InfoPopup settingKey={settingKey} darkMode={darkMode} />
        </div>
        <Toggle active={active} onClick={onToggle} />
    </div>
);
